'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AnorexicBmiCalculator() {
  const t = useTranslations("tools.anorexicBmiCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentUnit, setCurrentUnit] = useState<'imperial' | 'metric'>('imperial');
  const [inputs, setInputs] = useState({
    age: 20,
    gender: 'female' as 'female' | 'male',
    feet: 5,
    inches: 10,
    pounds: 160,
    cm: 180,
    kg: 60,
  });
  const [results, setResults] = useState<{
    bmi: number;
    category: string;
    severity: 'normal' | 'mild' | 'moderate' | 'severe' | 'extreme';
    statusTitle: string;
    statusDescription: string;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto' },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
      { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
    ),
    inputSection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    unitTabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.5rem',
      background: '#f1f5f9',
      padding: '0.25rem',
      borderRadius: '0.5rem',
    },
    unitTab: (isActive: boolean) => getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        fontWeight: 500,
        cursor: 'pointer',
        borderRadius: '0.375rem',
        transition: 'all 0.2s',
        background: isActive ? 'white' : 'transparent',
        color: isActive ? '#2563eb' : '#64748b',
        boxShadow: isActive ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none',
      },
      { padding: '0.5rem 0.75rem', fontSize: '0.9rem' },
      { padding: '0.5rem 1rem', fontSize: '0.95rem' },
      { padding: '0.5rem 1rem', fontSize: '1rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.2s',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.8rem 1.1rem', fontSize: '1.05rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.1rem' }
    ),
    heightInputs: getResponsiveStyle(
      { display: 'grid', gap: '0.75rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
    },
    radioLabel: getResponsiveStyle(
      { display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#334155' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    radio: {
      marginRight: '0.5rem',
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1.125rem 2.5rem', fontSize: '1.15rem' }
    ),
    resultsSection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.6rem' }
    ),
    bmiDisplay: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    bmiValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb', marginBottom: '0.5rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.75rem' },
      { fontSize: '3rem' }
    ),
    bmiLabel: getResponsiveStyle(
      { color: '#64748b' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    statusCard: (severity: string) => {
      const severityColors = {
        normal: { bg: '#f0fdf4', border: '#22c55e', text: '#16a34a' },
        mild: { bg: '#fef3c7', border: '#f59e0b', text: '#d97706' },
        moderate: { bg: '#fed7aa', border: '#f97316', text: '#ea580c' },
        severe: { bg: '#fee2e2', border: '#ef4444', text: '#dc2626' },
        extreme: { bg: '#fce7f3', border: '#dc2626', text: '#be123c' },
      };
      const colors = severityColors[severity as keyof typeof severityColors] || severityColors.normal;

      return getResponsiveStyle(
        {
          borderRadius: '0.75rem',
          marginBottom: '1.5rem',
          borderLeft: `4px solid ${colors.border}`,
          background: colors.bg,
        },
        { padding: '1.25rem' },
        { padding: '1.4rem' },
        { padding: '1.5rem' }
      );
    },
    statusTitle: (severity: string) => {
      const severityColors = {
        normal: '#16a34a',
        mild: '#d97706',
        moderate: '#ea580c',
        severe: '#dc2626',
        extreme: '#be123c',
      };
      const color = severityColors[severity as keyof typeof severityColors] || severityColors.normal;

      return getResponsiveStyle(
        { fontWeight: 700, marginBottom: '0.5rem', color },
        { fontSize: '1rem' },
        { fontSize: '1.05rem' },
        { fontSize: '1.1rem' }
      );
    },
    statusDescription: getResponsiveStyle(
      { color: '#475569', lineHeight: 1.6 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    infoGrid: {
      display: 'grid',
      gap: '1rem',
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
    },
    infoLabel: getResponsiveStyle(
      { color: '#64748b', fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    infoValue: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    warningBox: getResponsiveStyle(
      {
        background: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '0.75rem',
        marginTop: '1.5rem',
      },
      { padding: '1rem' },
      { padding: '1.15rem' },
      { padding: '1.25rem' }
    ),
    warningTitle: getResponsiveStyle(
      { color: '#92400e', display: 'block', marginBottom: '0.5rem', fontWeight: 600 },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    warningText: getResponsiveStyle(
      { color: '#78350f', margin: 0, lineHeight: 1.6 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    insightCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.8)',
        borderLeft: `4px solid ${colors.primary}`,
        borderRadius: '0.5rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
      },
      { padding: '1.25rem' },
      { padding: '1.4rem' },
      { padding: '1.5rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.2s',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightM: number;
    let weightKg: number;

    if (currentUnit === 'imperial') {
      const totalInches = (inputs.feet * 12) + inputs.inches;
      heightM = totalInches * 0.0254;
      weightKg = inputs.pounds * 0.453592;
    } else {
      heightM = inputs.cm / 100;
      weightKg = inputs.kg;
    }

    if (heightM <= 0 || weightKg <= 0) {
      alert(t('validationError'));
      return;
    }

    const bmi = weightKg / (heightM * heightM);

    let severity: 'normal' | 'mild' | 'moderate' | 'severe' | 'extreme';
    let category: string;
    let statusTitle: string;
    let statusDescription: string;

    if (bmi < 15) {
      severity = 'extreme';
      category = t('categoryExtreme');
      statusTitle = t('statusTitleExtreme');
      statusDescription = t('statusDescriptionExtreme');
    } else if (bmi >= 15 && bmi < 16) {
      severity = 'severe';
      category = t('categorySevere');
      statusTitle = t('statusTitleSevere');
      statusDescription = t('statusDescriptionSevere');
    } else if (bmi >= 16 && bmi < 17) {
      severity = 'moderate';
      category = t('categoryModerate');
      statusTitle = t('statusTitleModerate');
      statusDescription = t('statusDescriptionModerate');
    } else if (bmi >= 17 && bmi < 17.5) {
      severity = 'mild';
      category = t('categoryMild');
      statusTitle = t('statusTitleMild');
      statusDescription = t('statusDescriptionMild');
    } else if (bmi >= 17.5 && bmi < 18.5) {
      severity = 'mild';
      category = t('categoryUnderweight');
      statusTitle = t('statusTitleUnderweight');
      statusDescription = t('statusDescriptionUnderweight');
    } else if (bmi >= 18.5 && bmi < 25) {
      severity = 'normal';
      category = t('categoryNormal');
      statusTitle = t('statusTitleNormal');
      statusDescription = t('statusDescriptionNormal');
    } else if (bmi >= 25 && bmi < 30) {
      severity = 'normal';
      category = t('categoryOverweight');
      statusTitle = t('statusTitleOverweight');
      statusDescription = t('statusDescriptionOverweight');
    } else {
      severity = 'normal';
      category = t('categoryObese');
      statusTitle = t('statusTitleObese');
      statusDescription = t('statusDescriptionObese');
    }

    setResults({
      bmi,
      category,
      severity,
      statusTitle,
      statusDescription,
    });

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>🧮 {t('toolTitle')}</h2>
            <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <div style={styles.calculatorGrid}>
            <div style={styles.inputSection}>
              <div style={styles.unitTabs}>
                <button
                  type="button"
                  style={styles.unitTab(currentUnit === 'imperial')}
                  onClick={() => setCurrentUnit('imperial')}
                >
                  {t('unitImperial')}
                </button>
                <button
                  type="button"
                  style={styles.unitTab(currentUnit === 'metric')}
                  onClick={() => setCurrentUnit('metric')}
                >
                  {t('unitMetric')}
                </button>
              </div>

              <form onSubmit={handleCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="age">{t('labelAge')}</label>
                  <input
                    type="number"
                    id="age"
                    min="2"
                    max="120"
                    value={inputs.age}
                    onChange={(e) => setInputs({ ...inputs, age: parseInt(e.target.value) })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelGender')}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={inputs.gender === 'female'}
                        onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'female' | 'male' })}
                        style={styles.radio}
                      />
                      {t('genderFemale')}
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={inputs.gender === 'male'}
                        onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'female' | 'male' })}
                        style={styles.radio}
                      />
                      {t('genderMale')}
                    </label>
                  </div>
                </div>

                {currentUnit === 'imperial' ? (
                  <>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('labelHeight')}</label>
                      <div style={styles.heightInputs}>
                        <input
                          type="number"
                          min="0"
                          max="8"
                          value={inputs.feet}
                          onChange={(e) => setInputs({ ...inputs, feet: parseInt(e.target.value) || 0 })}
                          placeholder={t('placeholderFeet')}
                          style={styles.input}
                        />
                        <input
                          type="number"
                          min="0"
                          max="11"
                          value={inputs.inches}
                          onChange={(e) => setInputs({ ...inputs, inches: parseInt(e.target.value) || 0 })}
                          placeholder={t('placeholderInches')}
                          style={styles.input}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="pounds">{t('labelWeightPounds')}</label>
                      <input
                        type="number"
                        id="pounds"
                        min="1"
                        step="0.1"
                        value={inputs.pounds}
                        onChange={(e) => setInputs({ ...inputs, pounds: parseFloat(e.target.value) || 0 })}
                        style={styles.input}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="cm">{t('labelHeightCm')}</label>
                      <input
                        type="number"
                        id="cm"
                        min="1"
                        step="0.1"
                        value={inputs.cm}
                        onChange={(e) => setInputs({ ...inputs, cm: parseFloat(e.target.value) || 0 })}
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="kg">{t('labelWeightKg')}</label>
                      <input
                        type="number"
                        id="kg"
                        min="1"
                        step="0.1"
                        value={inputs.kg}
                        onChange={(e) => setInputs({ ...inputs, kg: parseFloat(e.target.value) || 0 })}
                        style={styles.input}
                        required
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  style={styles.calculateBtn}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={styles.resultsSection} id="results">
                <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

                <div style={styles.bmiDisplay}>
                  <div style={styles.bmiValue}>{results.bmi.toFixed(2)}</div>
                  <div style={styles.bmiLabel}>{t('bmiLabel')}</div>
                </div>

                <div style={styles.statusCard(results.severity)}>
                  <div style={styles.statusTitle(results.severity)}>{results.statusTitle}</div>
                  <div style={styles.statusDescription}>{results.statusDescription}</div>
                </div>

                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t('healthyRangeLabel')}</span>
                    <span style={styles.infoValue}>{t('healthyRangeValue')}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t('yourCategoryLabel')}</span>
                    <span style={styles.infoValue}>{results.category}</span>
                  </div>
                </div>

                <div style={styles.warningBox}>
                  <strong style={styles.warningTitle}>⚠️ {t('disclaimerTitle')}</strong>
                  <p style={styles.warningText}>{t('disclaimerText')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <div style={styles.insightCard}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginBottom: '0.75rem' }}>
            {t('insight1Title')}
          </h3>
          <p style={styles.paragraph}>{t('insight1Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginBottom: '0.75rem' }}>
            {t('insight2Title')}
          </h3>
          <p style={styles.paragraph}>{t('insight2Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginBottom: '0.75rem' }}>
            {t('insight3Title')}
          </h3>
          <p style={styles.paragraph}>{t('insight3Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginBottom: '0.75rem' }}>
            {t('insight4Title')}
          </h3>
          <p style={styles.paragraph}>{t('insight4Text')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem' }}>
          {t('detailsBmiRangesTitle')}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBmiRange1')}</li>
          <li style={styles.listItem}>{t('detailsBmiRange2')}</li>
          <li style={styles.listItem}>{t('detailsBmiRange3')}</li>
          <li style={styles.listItem}>{t('detailsBmiRange4')}</li>
          <li style={styles.listItem}>{t('detailsBmiRange5')}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem' }}>
          {t('detailsLimitationsTitle')}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsLimitation1')}</li>
          <li style={styles.listItem}>{t('detailsLimitation2')}</li>
          <li style={styles.listItem}>{t('detailsLimitation3')}</li>
          <li style={styles.listItem}>{t('detailsLimitation4')}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem' }}>
          {t('detailsBestPracticesTitle')}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBestPractice1')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice2')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice3')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice4')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.calculator.net/anorexic-bmi-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.nationaleatingdisorders.org/bmi/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8837073/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://withinhealth.com/learn/articles/the-limitations-of-diagnosing-anorexia-with-bmi"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://anorexiafamily.com/bmi-weight-for-height-wfh/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}